'use client'
import { EnvironmentBadge } from '@/components/environment-badge'
import FileUploadModal from '@/components/file-uploader/file-uploader'
import {
  AdminButton,
  AnalyticsButton,
  navbarLinkClass,
  SuperAdminButton,
  UserButton
} from '@/components/navbar-next'
import { useAuthContext } from '@/hooks/use-auth-context'
import { Group } from '@mantine/core'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { FC, memo, useMemo, useState } from 'react'

import logo from 'public/logo.png'
import { ClaimManagementDropdown } from './claim-management-dropdown'
import { OrgDropdown } from './org-dropdown'
import { PartnerDropdown } from './partner-dropdown'
import { AdminRoleID, SuperAdminRoleID } from '@/constants/role-ids'

export const Navbar: FC = memo(() => {
  const [isOpen, setIsOpen] = useState(false)

  const pathname = usePathname()
  const { account, organization } = useAuthContext()

  // Ingestion is cross-tenant by nature — an arrival is triaged before its owning
  // partnership is even resolved — so its menu entry is super-admin only, matching the
  // API that serves it. Price Sync sits in the Administration dropdown, Celerity-gated.
  const isSuperAdmin = account?.Role.ID === SuperAdminRoleID
  const isAdmin = useMemo(
    () =>
      account?.Role.ID === SuperAdminRoleID || account?.Role.ID === AdminRoleID,
    [account]
  )

  if (pathname === '/signin') return <></>

  return (
    <div>
      <div className="flex items-center justify-between bg-[var(--mantine-color-celerityBrand-9)] p-3 text-white">
        <div className="flex items-center">
          <div>
            <Link href="/">
              <Image
                src={logo}
                alt="Celerity Enterprises"
                height="50"
                width="50"
                priority
                className="mr-3"
              />
            </Link>
          </div>
          <div>
            <div className="flex">
              <Link href="/agreements" className={navbarLinkClass}>
                Agreements
              </Link>
              <ClaimManagementDropdown />
              {isAdmin && <AdminButton />}
              <AnalyticsButton
                organizationType={organization?.OrganizationType}
                hasImpactReportAccess={organization?.ImpactReportEnabled}
              />
            </div>
          </div>
        </div>
        <Group>
          <OrgDropdown />
          <PartnerDropdown />
          {isSuperAdmin && <SuperAdminButton />}
          {account && organization && (
            <UserButton
              firstName={account?.FirstName ?? ''}
              lastName={account?.LastName ?? ''}
              role={account?.Role?.Name ?? ''}
              orgName={organization?.Name ?? ''}
              setFileUploadModal={setIsOpen}
            />
          )}
          <EnvironmentBadge className="mr-3" />
        </Group>
      </div>
      {account && <FileUploadModal isOpen={isOpen} setIsOpen={setIsOpen} />}
    </div>
  )
})
